import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  CircularProgress,
  Toolbar,
  IconButton,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  MoveUp as MoveIcon,
  Hive as HiveIcon,
} from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from '@hooks/redux'
import {
  fetchApiariesAndGroups,
  updateHive,
} from '@store/slices/locationsSlice'
import { Location, Hive } from '@types'

/**
 * ApiaryManagement Page Component
 * 
 * Allows users to move hives between apiaries for better organization.
 * Features:
 * - Select source apiary and view all hives
 * - Select multiple hives to move
 * - Choose destination apiary
 * - Batch move operation
 * - Visual hive selection interface
 * 
 * @component
 */
const ApiaryManagement: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const dispatch = useAppDispatch()

  const { apiaries, loadingState, error } = useAppSelector((state) => state.locations)

  const [sourceApiaryId, setSourceApiaryId] = useState<number | null>(null)
  const [destinationApiaryId, setDestinationApiaryId] = useState<number | null>(null)
  const [selectedHiveIds, setSelectedHiveIds] = useState<number[]>([])
  const [allSelected, setAllSelected] = useState(false)
  const [moving, setMoving] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  useEffect(() => {
    dispatch(fetchApiariesAndGroups())
  }, [dispatch])

  useEffect(() => {
    // If ID is provided in URL, pre-select that apiary
    if (id && apiaries.length > 0) {
      const apiaryId = parseInt(id)
      if (apiaries.find(a => a.id === apiaryId)) {
        setSourceApiaryId(apiaryId)
      }
    }
  }, [id, apiaries])

  const sourceApiary = apiaries.find(a => a.id === sourceApiaryId)
  const availableHives = sourceApiary?.hives || []
  const editableHives = availableHives.filter(hive => hive.editable)

  const handleSelectAll = (checked: boolean) => {
    setAllSelected(checked)
    if (checked) {
      setSelectedHiveIds(editableHives.map(hive => hive.id))
    } else {
      setSelectedHiveIds([])
    }
  }

  const handleSelectHive = (hiveId: number, checked: boolean) => {
    if (checked) {
      setSelectedHiveIds(prev => [...prev, hiveId])
    } else {
      setSelectedHiveIds(prev => prev.filter(id => id !== hiveId))
      setAllSelected(false)
    }
  }

  const handleMoveHives = async () => {
    if (!destinationApiaryId || selectedHiveIds.length === 0) return

    setMoving(true)
    setSuccessMessage(null)

    try {
      // Move each selected hive to the destination apiary
      for (const hiveId of selectedHiveIds) {
        await dispatch(updateHive({
          id: hiveId,
          data: { location_id: destinationApiaryId }
        }))
      }

      setSuccessMessage(`Successfully moved ${selectedHiveIds.length} hive${selectedHiveIds.length > 1 ? 's' : ''} to the new apiary`)
      
      // Reset selections
      setSelectedHiveIds([])
      setAllSelected(false)
      setDestinationApiaryId(null)
      
      // Refresh data
      dispatch(fetchApiariesAndGroups())
    } catch (err) {
      console.error('Failed to move hives:', err)
    } finally {
      setMoving(false)
    }
  }

  const canMove = sourceApiaryId && destinationApiaryId && selectedHiveIds.length > 0 && sourceApiaryId !== destinationApiaryId

  if (loadingState === 'pending') {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    )
  }

  return (
    <Container sx={{ py: 3 }}>
      {/* Toolbar */}
      <Toolbar sx={{ px: 0, mb: 2, justifyContent: 'space-between' }}>
        <IconButton onClick={() => navigate('/apiaries')}>
          <BackIcon />
        </IconButton>
        <Button
          variant="contained"
          startIcon={moving ? <CircularProgress size={20} /> : <MoveIcon />}
          onClick={handleMoveHives}
          disabled={!canMove || moving}
        >
          Move {selectedHiveIds.length} Hive{selectedHiveIds.length !== 1 ? 's' : ''}
        </Button>
      </Toolbar>

      {/* Header */}
      <Typography variant="h4" component="h1" gutterBottom>
        Apiary Management
      </Typography>

      <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
        Move hives between apiaries to better organize your beekeeping operation.
      </Typography>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Source Apiary Selection */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="overline" display="block" gutterBottom>
                Current Apiary
              </Typography>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Select Source Apiary</InputLabel>
                <Select
                  value={sourceApiaryId || ''}
                  onChange={(e) => {
                    setSourceApiaryId(e.target.value as number)
                    setSelectedHiveIds([])
                    setAllSelected(false)
                  }}
                  label="Select Source Apiary"
                >
                  {apiaries.map((apiary) => (
                    <MenuItem key={apiary.id} value={apiary.id}>
                      {apiary.name} ({apiary.hives.length} hives)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {sourceApiary && (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={allSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        disabled={editableHives.length === 0}
                      />
                    }
                    label="Select All Hives"
                    sx={{ mb: 2 }}
                  />

                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {selectedHiveIds.length} of {editableHives.length} hives selected
                  </Typography>

                  {editableHives.length === 0 ? (
                    <Alert severity="info">
                      No editable hives in this apiary
                    </Alert>
                  ) : (
                    <List dense>
                      {editableHives.map((hive) => (
                        <ListItem key={hive.id} divider>
                          <ListItemIcon>
                            <HiveIcon sx={{ color: hive.color || theme.palette.primary.main }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={hive.name}
                            secondary={`Last inspection: ${hive.last_inspection_date || 'Never'}`}
                          />
                          <ListItemSecondaryAction>
                            <Checkbox
                              checked={selectedHiveIds.includes(hive.id)}
                              onChange={(e) => handleSelectHive(hive.id, e.target.checked)}
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Destination Apiary Selection */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="overline" display="block" gutterBottom>
                New Apiary
              </Typography>

              <FormControl fullWidth>
                <InputLabel>Select Destination Apiary</InputLabel>
                <Select
                  value={destinationApiaryId || ''}
                  onChange={(e) => setDestinationApiaryId(e.target.value as number)}
                  label="Select Destination Apiary"
                  disabled={!sourceApiaryId}
                >
                  {apiaries
                    .filter(apiary => apiary.id !== sourceApiaryId)
                    .map((apiary) => (
                      <MenuItem key={apiary.id} value={apiary.id}>
                        {apiary.name} ({apiary.hives.length} hives)
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {destinationApiaryId && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="textSecondary">
                    The selected hives will be moved to this apiary. This action will:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                    <Typography component="li" variant="body2" color="textSecondary">
                      Update the hive location
                    </Typography>
                    <Typography component="li" variant="body2" color="textSecondary">
                      Maintain all hive data and inspection history
                    </Typography>
                    <Typography component="li" variant="body2" color="textSecondary">
                      Update any associated sensor configurations
                    </Typography>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Summary Card */}
      {selectedHiveIds.length > 0 && destinationApiaryId && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="overline" display="block" gutterBottom>
              Move Summary
            </Typography>
            <Typography variant="body1">
              Moving <strong>{selectedHiveIds.length} hive{selectedHiveIds.length > 1 ? 's' : ''}</strong> from{' '}
              <strong>{sourceApiary?.name}</strong> to{' '}
              <strong>{apiaries.find(a => a.id === destinationApiaryId)?.name}</strong>
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  )
}

export default ApiaryManagement
